'use client';

import type { FC, SelectHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { RiArrowDownSLine } from '../icons';
import { cn } from '../../utils/cn';

const selectVariants = cva(
  'peer w-full appearance-none truncate border border-gray rounded-xl bg-bg-secondary text-label-primary font-[inherit] cursor-pointer transition-[border-color] duration-200 ease-smooth hover:border-accent-blue focus:outline-none focus:border-accent-blue disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        s: 'ps-3 pe-9 py-2 text-sm',
        m: 'ps-4 pe-11 py-3 text-base',
        l: 'ps-5 pe-12 py-4 text-lg',
      },
    },
    defaultVariants: {
      size: 'm',
    },
  },
);

const chevronVariants = cva(
  'pointer-events-none absolute inset-y-0 my-auto text-label-secondary transition-colors peer-hover:text-label-primary peer-focus:text-label-primary peer-disabled:opacity-50',
  {
    variants: {
      size: {
        s: 'end-3 size-4',
        m: 'end-4 size-5',
        l: 'end-5 size-6',
      },
    },
    defaultVariants: {
      size: 'm',
    },
  },
);

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: 's' | 'm' | 'l';
  containerClassName?: string;
}

export const Select: FC<SelectProps> = ({ children, size = 'm', className, containerClassName, ...props }) => (
  <span className={cn('relative flex w-full min-w-0', containerClassName)}>
    <select className={cn(selectVariants({ size }), className)} {...props}>
      {children}
    </select>
    <RiArrowDownSLine aria-hidden className={chevronVariants({ size })} />
  </span>
);
